import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Shield, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RolesTable } from "@/components/roles/RolesTable";
import { RoleModal } from "@/components/roles/RoleModal";
import { RoleFilters } from "@/components/roles/RoleFilters";
import { RoleCard } from "@/components/roles/RoleCard";
import { rolesService } from "@/services/roles";
import { Role, CreateRoleRequest } from "@/types/roles";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

export default function RolesPage() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [statistics, setStatistics] = useState({
    totalRoles: 0,
    activeRoles: 0,
    totalUsers: 0,
    systemRoles: 0,
    customRoles: 0,
  });

  const { toast } = useToast();
  const { user, company, canManageRoles: canManageRolesFn } = useAuth();

  const companySlug = company?.slug || "";
  const companyId = company?.id || "";
  const canManageRoles = canManageRolesFn ? canManageRolesFn() : false;

  // Función para cargar roles y estadísticas
  const loadRoles = async () => {
    if (!companySlug) return;

    try {
      setLoading(true);
      const { data } = await rolesService.getRoles(companySlug);
      setRoles(data);

      const systemRolesCount = data.filter((r) => r.is_system_role).length;
      setStatistics({
        totalRoles: data.length,
        activeRoles: data.filter((r) => r.is_active !== false).length,
        totalUsers: data.reduce((sum, r) => sum + (r.users_count || 0), 0),
        systemRoles: systemRolesCount,
        customRoles: data.length - systemRolesCount,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudieron cargar los roles",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companySlug) loadRoles();
  }, [companySlug]);

  // Filtrado de roles
  const filteredRoles = roles.filter((role) => {
    const matchesSearch =
      role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (role.description || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Crear o actualizar roles
  const handleCreateRole = async (roleData: Partial<Role>) => {
    if (!companySlug || !companyId) {
      toast({
        title: "Error",
        description: "Información de empresa incompleta",
        variant: "destructive",
      });
      return;
    }

    try {
      setSaving(true);

      if (!user?.id) throw new Error("Usuario no identificado");

      const createData = {
        company_id: companyId,
        slug: companySlug,
        name: roleData.name || "",
        description: roleData.description || "",
        is_system_role: false,
        permissions: (roleData.permissions as any) || [],
        userId: user.id,
      };

      await rolesService.createRole(createData);

      toast({ title: "Éxito", description: "Rol creado correctamente" });
      await loadRoles();
      setIsModalOpen(false);
    } catch (error: any) {
      let errorMessage = error.message || "No se pudo crear el rol";
      if (errorMessage.includes("Ya existe un rol"))
        errorMessage = "Ya existe un rol con este nombre.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async (roleId: string, roleData: Partial<Role>) => {
    try {
      setSaving(true);

      if (!user?.id) throw new Error("Usuario no identificado");

      await rolesService.updateRole(roleId, {
        slug: companySlug,
        userId: user.id,
        name: roleData.name || "",
        description: roleData.description || "",
        permissions: (roleData.permissions as any) || [],
      });
      toast({ title: "Éxito", description: "Rol actualizado correctamente" });
      await loadRoles();
      setIsModalOpen(false);
    } catch (error: any) {
      let errorMessage = error.message || "No se pudo actualizar el rol";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Eliminar roles
  const handleDeleteRole = (role: Role) => {
    if (role.is_system_role) {
      toast({
        title: "Acción no permitida",
        description: "No se pueden eliminar roles del sistema",
        variant: "destructive",
      });
      return;
    }
    if (role.users_count && role.users_count > 0) {
      toast({
        title: "Acción no permitida",
        description: `El rol "${role.name}" tiene usuarios asignados`,
        variant: "destructive",
      });
      return;
    }

    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!roleToDelete) return;

    try {
      if (!user?.id) throw new Error("Usuario no identificado");
      await rolesService.deleteRole(roleToDelete.id, companySlug, user.id);
      toast({ title: "Éxito", description: "Rol eliminado correctamente" });
      await loadRoles();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar el rol",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    }
  };

  // Editar roles
  const handleEditRole = (role: Role) => {
    if (!canManageRoles || role.is_system_role) {
      toast({
        title: "Acción no permitida",
        description: "No puedes editar este rol",
        variant: "destructive",
      });
      return;
    }
    setEditingRole(role);
    setIsModalOpen(true);
  };

  const handleSaveRole = async (roleData: Partial<Role>) => {
    if (editingRole) await handleUpdateRole(editingRole.id, roleData);
    else await handleCreateRole(roleData);
  };

  if (!companySlug) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-center">
        <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">
          Información incompleta
        </h3>
        <p className="text-slate-600">
          No se pudo cargar la información de la empresa.
        </p>
      </div>
    );
  }

  // Pagination
  const ITEMS_PER_PAGE = 12;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(filteredRoles.length / ITEMS_PER_PAGE);
  const paginatedRoles = filteredRoles.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className="w-full min-h-[calc(100vh-6rem)] rounded-[32px] bg-white border border-slate-200/60 shadow-[0_4px_40px_-12px_rgba(0,0,0,0.06)] overflow-hidden flex flex-col relative z-10">
      <div className="p-8 sm:p-10 flex-1 flex flex-col">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-blue-100 p-2.5 rounded-2xl">
                <Shield className="h-7 w-7 text-blue-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                Gestión de Roles
              </h1>
            </div>
            <p className="text-slate-500 text-sm md:text-base ml-1">
              Administra roles y permisos del sistema
              {user && (
                <span className="ml-2 text-blue-600 hidden sm:inline font-medium">
                  (Tu rol: {user.role?.name || "No definido"})
                </span>
              )}
            </p>
          </div>

          {canManageRoles && (
            <Button
              onClick={() => {
                setEditingRole(null);
                setIsModalOpen(true);
              }}
              className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white rounded-2xl shadow-lg shadow-slate-900/20 px-6 h-12 text-sm font-medium transition-all"
            >
              <Plus className="h-5 w-5 mr-2" />
              Nuevo Rol
            </Button>
          )}
        </div>

        {/* Filtros */}
        <div className="mb-6">
          <RoleFilters
            searchQuery={searchQuery}
            onSearchChange={(q) => {
              setSearchQuery(q);
              setCurrentPage(1);
            }}
            statistics={statistics}
          />
        </div>

        {/* Lista de roles */}
        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-4" />
            <p className="text-slate-500 font-medium">Cargando roles...</p>
          </div>
        ) : (
          <div className="flex-1 flex flex-col">
            <div className="md:hidden space-y-4">
              {paginatedRoles.length === 0 ? (
                <div className="text-center py-20 bg-slate-50/50 rounded-3xl border border-dashed border-slate-200">
                  <Shield className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    No hay roles
                  </h3>
                  <p className="text-slate-500 mb-6">
                    {searchQuery
                      ? "No se encontraron roles con los filtros aplicados"
                      : "No hay roles creados en el sistema"}
                  </p>
                  {canManageRoles && (
                    <Button 
                      onClick={() => setIsModalOpen(true)}
                      className="bg-slate-900 hover:bg-slate-800 text-white rounded-2xl"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Crear primer rol
                    </Button>
                  )}
                </div>
              ) : (
                paginatedRoles.map((role) => (
                  <RoleCard
                    key={role.id}
                    role={role}
                    onEdit={handleEditRole}
                    onDelete={handleDeleteRole}
                  />
                ))
              )}
            </div>

            <div className="hidden md:block flex-1">
              {paginatedRoles.length === 0 ? (
                <div className="text-center py-20 bg-slate-50/50 rounded-3xl border border-dashed border-slate-200">
                  <Shield className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    No hay roles
                  </h3>
                  <p className="text-slate-500 mb-6">
                    {searchQuery
                      ? "No se encontraron roles con los filtros aplicados"
                      : "No hay roles creados en el sistema"}
                  </p>
                  {canManageRoles && (
                    <Button 
                      onClick={() => setIsModalOpen(true)}
                      className="bg-slate-900 hover:bg-slate-800 text-white rounded-2xl"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Crear primer rol
                    </Button>
                  )}
                </div>
              ) : (
                <RolesTable
                  roles={paginatedRoles}
                  onEdit={handleEditRole}
                  onDelete={handleDeleteRole}
                  canManageRoles={canManageRoles}
                />
              )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                <span className="text-sm font-medium text-slate-500 bg-slate-50 px-4 py-2 rounded-xl">
                  Mostrando {paginatedRoles.length} de {filteredRoles.length} roles
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="rounded-xl border-slate-200 hover:bg-slate-50 font-medium"
                  >
                    Anterior
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className={`rounded-xl w-10 ${
                        currentPage === page
                          ? "bg-slate-900 text-white shadow-md shadow-slate-900/20"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {page}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="rounded-xl border-slate-200 hover:bg-slate-50 font-medium"
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Modal */}
        {canManageRoles && (
          <RoleModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setEditingRole(null);
            }}
            onSave={handleSaveRole}
            role={editingRole}
            companySlug={companySlug}
            companyId={companyId}
            loading={saving}
          />
        )}

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="rounded-[24px]">
            <AlertDialogHeader>
              <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer. Esto eliminará permanentemente
                el rol "{roleToDelete?.name}" y revocará los permisos asociados
                para los usuarios que lo tengan asignado.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-xl">Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 rounded-xl"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
